import React, { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Wallet, Info } from 'lucide-react';
import { useSidebar } from '../../contexts/SidebarContext';
import MainLayout from '../layout/MainLayout';
import ContentArea from '../layout/ContentArea';
import Sidebar from '../layout/Sidebar';
import { fetchUserCredits } from '../../api/user';
import { fetchSubscriptionPlans, subscribeToPlan } from '../../api/subscription';
import Toast from '../common/Toast';
import PricingCard from './PricingCard';
import PillButton from '../common/button/PillButton';
import UsageModal from './UsageModal';

import styles from '../store/styles/StoreContainer.module.css';
import planStyles from './styles/SubscriptionPlans.module.css';

const SubscriptionContainer = () => {
    const [credits, setCredits] = useState(null);
    const [plans, setPlans] = useState([]);
    const [currentPlan, setCurrentPlan] = useState(null);
    const [planExpiresAt, setPlanExpiresAt] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const { setPage: setSidebarPage } = useSidebar();
    const [toastMessage, setToastMessage] = useState(null);
    const [isUsageOpen, setIsUsageOpen] = useState(false);

    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);
            const [creditsData, plansData] = await Promise.all([
                fetchUserCredits(),
                fetchSubscriptionPlans(),
            ]);
            setCredits(creditsData);
            setPlans(plansData.plans);
            setCurrentPlan(plansData.currentPlan);
            setPlanExpiresAt(plansData.expiresAt);
        } catch (error) {
            console.error('Error loading subscription data:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        document.title = 'JetChats - Subscription';
        setSidebarPage('subscription');
    }, [setSidebarPage]);

    const handleNewChat = useCallback(() => {
        navigate('/chats');
    }, [navigate]);

    const handleSubscribe = useCallback(async (planId) => {
        try {
            const response = await subscribeToPlan(planId);
            if (response.success) {
                setToastMessage({
                    message: 'This feature is not available at the moment. When it will be available, we will send you a notification to your email.',
                    duration: 6000,
                    type: 'info',
                });
            }
        } catch (error) {
            console.error('Error subscribing to plan:', error);
            setToastMessage({
                message: 'Error subscribing to plan',
                type: 'error',
            });
        }
    }, []);

    const formatExpirationDate = useCallback(date => {
        if (!date) return null;

        const expirationDate = new Date(date);
        return expirationDate.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    }, []);

    if (isLoading) {
        return (
            <MainLayout>
                <Sidebar handleNewChat={handleNewChat} />
                <ContentArea>
                    <div className={styles.container}>
                        <div>Loading...</div>
                    </div>
                </ContentArea>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <Sidebar handleNewChat={handleNewChat} />
            <ContentArea>
                <div className={styles.container}>
                    <div className={styles.content}>
                        <div className={styles.header}>
                            <h1>Subscription</h1>
                            <p>Manage your subscription plan and credits.</p>
                        </div>

                        {credits && (
                            <div className={planStyles.section}>
                                <div className={`${planStyles.card} ${planStyles.creditsCard}`}>
                                    <Wallet size={20} />
                                    <div className={planStyles.info}>
                                        <span className={planStyles.name}>Available Credits</span>
                                        <span className={planStyles.creditsAmount}>
                                            {currentPlan?.name === 'free'
                                                ? credits.subscriptionBalance.toLocaleString()
                                                : credits.total.toLocaleString()}
                                        </span>
                                        <div className={planStyles.creditsSplit}>
                                            <span>Subscription: <b>{credits.subscriptionBalance.toLocaleString()}</b></span>
                                            {currentPlan?.name === 'free' && (
                                                <span>Purchased: {credits.purchasedBalance.toLocaleString()} (frozen on the free plan)</span>
                                            )}
                                            {currentPlan?.name !== 'free' && (
                                                <span>Purchased: {credits.purchasedBalance.toLocaleString()}</span>
                                            )}
                                        </div>
                                        <div className={planStyles.creditsSplit} style={{ marginTop: '8px' }}>
                                            <span>
                                                Purchased Credits are available only with a paid plan (they are frozen on the free plan)
                                            </span>
                                        </div>
                                    </div>

                                    <div className={planStyles.usageButtonWrap}>
                                        <PillButton onClick={() => setIsUsageOpen(true)} size="small" variant="secondary">
                                            Usage
                                        </PillButton>
                                    </div>
                                </div>

                                <div className={planStyles.infoBox}>
                                    <Info size={18} />
                                    <p>
                                        Credits are used in this order: subscription credits first, then purchased credits. Subscription credits renew monthly. Purchased credits never expire and are only used when subscription credits are insufficient (they remain available even if you cancel your subscription, but can only be used with an active paid plan).
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className={planStyles.section}>
                            <h2>Current Plan</h2>
                            <div className={planStyles.card}>
                                <CreditCard size={20} />
                                <div className={planStyles.info}>
                                    <span className={planStyles.name}>
                                        {currentPlan?.name || 'Free'} Plan
                                    </span>
                                    <span className={planStyles.planCredits}>
                                        {currentPlan?.credits.toLocaleString()} credits per month
                                    </span>
                                    {planExpiresAt && (
                                        <span className={planStyles.planCredits}>
                                            {currentPlan?.cancelAtPeriodEnd 
                                                ? `Expires on ${formatExpirationDate(planExpiresAt)}`
                                                : `Renews on ${formatExpirationDate(planExpiresAt)}`
                                            }
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={planStyles.section}>
                            <h2>Available Plans</h2>
                            <div className={planStyles.plansGrid}>
                                {plans.map(plan => (
                                    <PricingCard
                                        key={plan.id}
                                        plan={plan}
                                        isCurrentPlan={currentPlan?.id === plan.id}
                                        onSubscribe={handleSubscribe}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {toastMessage && (
                    <Toast
                        message={toastMessage.message}
                        type={toastMessage.type}
                        onClose={() => setToastMessage(null)}
                        duration={toastMessage.duration}
                    />
                )}

                <UsageModal isOpen={isUsageOpen} onClose={() => setIsUsageOpen(false)} />
            </ContentArea>
        </MainLayout>
    );
};

export default memo(SubscriptionContainer);
